import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, Loader } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import type { UseMutationResult } from "@tanstack/react-query";
import { formatCurrency } from "@/utils/currency";
import { Button } from "@/components/ui/button";
import PaymentMethodSelector from "./payment-method-selector";
import AddCardForm from "./add-card-form";
import type {
  BillingInfo,
  CustomerPaymentMethod,
  PayPayload,
  RawGatewayConfig,
} from "../types";

interface Props {
  gatewayConfig: RawGatewayConfig;
  paymentMethods: CustomerPaymentMethod[];
  billingInfo: BillingInfo;
  contractId: string | number;
  totalAmount: number;
  invalidateKey: unknown[];
  payMutation: UseMutationResult<void, Error, PayPayload>;
  onSuccess: () => void;
  onAlreadyPaid: () => void;
}

const expand = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: "auto",
    opacity: 1,
    transition: {
      height: { duration: 0.35, ease: [0.4, 0, 0.2, 1] as [number, number, number, number] },
      opacity: { duration: 0.2, delay: 0.06 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.28, ease: [0.4, 0, 1, 1] as [number, number, number, number] },
      opacity: { duration: 0.15 },
    },
  },
};

/**
 * Whether a failed pay call means the invoice was settled while this tab sat open.
 *
 * The two flows abort with different statuses (the invoice endpoint 422s, the service-call
 * one 400s) and the message is translated, so both are accepted and both locales matched.
 * Matching prose is the weak part: the durable fix is a machine-readable code on the error
 * body, which neither endpoint sends today.
 */
const ALREADY_PAID_PHRASES = ["already been paid", "déjà été payée"];

export const isAlreadyPaidError = (status: number | undefined, message: string): boolean => {
  if (status !== 400 && status !== 422) return false;
  const haystack = message.toLowerCase();

  return ALREADY_PAID_PHRASES.some((phrase) => haystack.includes(phrase));
};

const PaymentPanel: FC<Props> = ({
  gatewayConfig,
  paymentMethods,
  billingInfo,
  contractId,
  totalAmount,
  invalidateKey,
  payMutation,
  onSuccess,
  onAlreadyPaid,
}) => {
  const { t, i18n } = useTranslation(["checkout"]);
  const methods = paymentMethods;

  const [selectedMethodId, setSelectedMethodId] = useState<
    string | number | null
  >(methods[0]?.id ?? null);
  const [addingNew, setAddingNew] = useState(methods.length === 0);
  const [gatewayError, setGatewayError] = useState<string | null>(null);

  const { mutate: pay, isPending: paying } = payMutation;

  const handlePay = (methodId: string | number) => {
    setGatewayError(null);
    pay(
      { payment_method_id: methodId },
      {
        onSuccess,
        onError: (err: any) => {
          const msg = err?.response?.data?.message ?? "";
          if (isAlreadyPaidError(err?.response?.status, msg)) {
            onAlreadyPaid();
          } else {
            setGatewayError(
              msg ||
                t(
                  "checkout:error-pay-failed",
                  "Payment failed. Please try again.",
                ),
            );
          }
        },
      },
    );
  };

  return (
    <div className="rounded-xl border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold mb-6">
        {t("checkout:payment-title", "Payment Information")}
      </h2>

      {methods.length > 0 && (
        <div className="mb-6">
          <p className="text-sm font-medium text-muted-foreground mb-3">
            {t("checkout:payment-select-method", "Select a payment method")}
          </p>
          <PaymentMethodSelector
            methods={methods}
            selectedId={selectedMethodId}
            contractId={contractId}
            invalidateKey={invalidateKey}
            addingNew={addingNew}
            onSelect={(id) => {
              setSelectedMethodId(id);
              setAddingNew(false);
              setGatewayError(null);
            }}
            onAddNew={() => {
              setAddingNew(true);
              setSelectedMethodId(null);
              setGatewayError(null);
            }}
            onRemoved={(removedId) => {
              if (selectedMethodId === removedId) setSelectedMethodId(null);
              setGatewayError(null);
            }}
          />
        </div>
      )}

      <AnimatePresence initial={false}>
        {addingNew && (
          <motion.div key="add-card" style={{ overflow: "hidden" }} {...expand}>
            <AddCardForm
              contractId={contractId}
              gatewayConfig={gatewayConfig}
              billingInfo={billingInfo}
              paying={paying}
              onSuccess={handlePay}
              onError={setGatewayError}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {gatewayError && (
          <motion.div
            key="gateway-error"
            style={{ overflow: "hidden" }}
            {...expand}
            className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
          >
            <AlertCircle className="size-4 mt-0.5 shrink-0" />
            <span dangerouslySetInnerHTML={{ __html: gatewayError }} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {!addingNew && selectedMethodId && (
          <motion.div
            key="pay-button"
            className="mt-6"
            style={{ overflow: "hidden" }}
            {...expand}
          >
            <Button
              type="button"
              size="lg"
              onClick={() => handlePay(selectedMethodId)}
              disabled={paying}
              className="w-full"
            >
              {paying && <Loader className="size-4 animate-spin" />}
              {t(
                "checkout:payment-pay-button",
                "Complete Payment - {{amount}}",
                {
                  amount: formatCurrency(totalAmount, i18n.language),
                },
              )}
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PaymentPanel;
